import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';

import {
  isBusyChat,
  isRefusedSend,
  withoutPendingTurn,
  withPendingTurn,
} from './chatMessages';
import { chatEndpoints } from './constants';
import { chatMutationKeys, chatQueryKeys } from './queryKeys';
import { refreshJobData } from './refreshJobData';
import type { ChatApi } from './types';

export interface SendChatMessageVariables {
  chatId: string;
  text: string;
  lastSeenReplyId: string | null;
  sentAt: string;
}

/**
 * Sends a message in a chat; it returns once Finlay has replied, or the reply failed. Meanwhile the chat shows the
 * message and a pending reply, as finlay-api saves them before calling the agent.
 */
export const useSendChatMessageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: chatMutationKeys.send,
    mutationFn: async ({
      chatId,
      text,
      lastSeenReplyId,
    }: SendChatMessageVariables) => {
      const { data } = await fetchClient.POST(chatEndpoints.messages, {
        params: { path: { id: chatId } },
        body: { text, lastSeenReplyId },
      });
      return data;
    },
    onMutate: async ({ chatId, text, sentAt }) => {
      const queryKey = chatQueryKeys.detail(chatId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ChatApi>(queryKey);
      if (previous) {
        queryClient.setQueryData(
          queryKey,
          withPendingTurn(previous, text, sentAt),
        );
      }
      return { previous };
    },
    onSuccess: async (chat, { chatId }) => {
      const queryKey = chatQueryKeys.detail(chatId);
      // A check started while the reply was pending must not land after it and show it pending again.
      await queryClient.cancelQueries({ queryKey });
      if (chat) queryClient.setQueryData(queryKey, chat);
      void refreshJobData(queryClient);
    },
    onError: (error, { chatId }, context) => {
      const queryKey = chatQueryKeys.detail(chatId);
      if (isBusyChat(error)) {
        // Another tab's turn holds the chat. The copy from before this send may predate that turn, so the chat shows
        // what finlay-api has instead: that turn, whose pending reply keeps it checking until the reply is in.
        queryClient.setQueryData<ChatApi>(
          queryKey,
          (current) => current && withoutPendingTurn(current),
        );
        return queryClient.invalidateQueries({ queryKey });
      }
      if (isRefusedSend(error)) {
        queryClient.setQueryData(queryKey, context?.previous);
        return;
      }
      // The connection broke, but the turn may be running: the chat shows what the server has.
      return queryClient.invalidateQueries({ queryKey });
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: chatQueryKeys.lists() }),
  });
};
